import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Check } from 'lucide-react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { PasswordInput } from '../components/PasswordInput.js';

export default function ResetPasswordPage() {
  const { t } = useAppTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage(t('auth.resetPassword.errors.passwordMismatch', 'Passwords do not match'));
      setStatus('error');
      return;
    }
    if (password.length < 8) {
      setMessage(t('auth.resetPassword.errors.passwordTooShort', 'Password must be at least 8 characters'));
      setStatus('error');
      return;
    }

    setIsSubmitting(true);
    setStatus('idle');
    setMessage('');

    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus('success');
        setMessage(t('auth.resetPassword.success', 'Your password has been reset successfully.'));
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setStatus('error');
        setMessage(data.error || t('auth.resetPassword.errors.failed', 'Failed to reset password'));
      }
    } catch {
      setStatus('error');
      setMessage(t('common.errors.genericRetry', 'An error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface dark:bg-main-dark flex items-center justify-center p-4">
      <div className="bg-card dark:bg-card-dark max-w-sm w-full rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-accent-100 dark:bg-accent-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-accent-600 dark:text-accent-400">
            <Lock className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark">
            {t('auth.resetPassword.title', 'Reset Password')}
          </h1>
        </div>

        {status === 'success' ? (
          <div className="text-center">
            <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-success-600 dark:text-success-400">
              <Check className="w-6 h-6" />
            </div>
            <p className="text-text-primary dark:text-text-primary-dark mb-4">{message}</p>
            <p className="text-sm text-text-muted dark:text-text-muted-dark">
              {t('auth.resetPassword.redirecting', 'Redirecting to login...')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">
                {t('auth.resetPassword.fields.newPassword', 'New Password')}
              </label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.resetPassword.placeholders.minPassword', 'Minimum 8 characters')}
                className="w-full px-5 py-3 rounded-lg border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">
                {t('auth.resetPassword.fields.confirmPassword', 'Confirm Password')}
              </label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.resetPassword.placeholders.repeatPassword', 'Repeat password')}
                className="w-full px-5 py-3 rounded-lg border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2"
                required
                minLength={8}
              />
            </div>

            {status === 'error' && (
              <div className="text-error-text dark:text-error-text-dark text-sm text-center">{message}</div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 rounded-full bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50"
            >
              {isSubmitting
                ? t('auth.resetPassword.actions.resetting', 'Resetting...')
                : t('auth.resetPassword.actions.submit', 'Set New Password')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
